import { useState } from 'react';
import Editable from './Editable';
import type { Translations, SeoData } from '../types';
import { assets } from '../assetsConfig';

interface FooterProps {
  t: Translations;
  seo: SeoData;
  isEditable: boolean;
  onUpdate: (key: string, val: string) => void;
}

export default function Footer({ t, seo, isEditable, onUpdate }: FooterProps) {
  const [activeModal, setActiveModal] = useState<'impressum' | 'datenschutz' | null>(null);

  const formatLegalText = (text: string) => {
    if (!text) return null;
    return text.split('\n\n').map((paragraph, i) => {
      const lines = paragraph.split('\n');
      return (
        <div key={i} className="mb-6">
          {lines.map((line, j) => {
            const isHeading = j === 0;
            const hasLink = line.match(/https?:\/\/[^\s]+/);
            
            return (
              <div key={j} className={isHeading ? "font-bold text-lg text-slate-800 mb-2" : "text-slate-600 mb-1"}>
                {hasLink ? (
                  <>
                    {line.split(hasLink[0])[0]}
                    <a href={hasLink[0]} target="_blank" rel="noopener noreferrer" className="text-[#c69c6d] hover:underline">
                      {hasLink[0]}
                    </a>
                    {line.split(hasLink[0])[1]}
                  </>
                ) : (
                  line
                )}
              </div>
            );
          })}
        </div>
      );
    });
  };

  return (
    <>
      <footer className="bg-[#125b60] text-white pt-16 pb-8 border-t-[8px] border-[#c69c6d]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8 items-start border-b border-white/10 pb-10">
            
            {/* Kolumna 1: Logo i opis (Wyrównanie do lewej) */}
            <div className="flex flex-col items-center md:items-start w-full text-center md:text-left">
              <div className="bg-white p-2 rounded-xl mb-4 inline-block">
                <img src={assets.img.logo} alt={seo.logoAlt} className="h-10 w-auto object-contain" />
              </div>
              <Editable 
                isEditable={isEditable} 
                value={t.footer_desc} 
                onChange={(v) => onUpdate('footer_desc', v)} 
                as="p" 
                className="text-slate-100 text-sm max-w-xs leading-relaxed" 
                multiline 
              />
            </div>

            {/* Kolumna 2: Social Media (Wyśrodkowana) */}
            <div className="flex flex-col items-center w-full text-center">
              <Editable 
                isEditable={isEditable} 
                value={t.footer_social_title} 
                onChange={(v) => onUpdate('footer_social_title', v)} 
                as="h3" 
                className="text-lg font-bold mb-4 text-white" 
              />
              <div className="flex gap-4 justify-center">
                <a href={t.social_fb_link} onClick={(e) => isEditable && e.preventDefault()} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-2xl hover:bg-[#1877F2] transition-all">
                  <i className="ri-facebook-fill" aria-hidden="true"></i>
                </a>
                <a href={t.social_messenger_link} onClick={(e) => isEditable && e.preventDefault()} target="_blank" rel="noopener noreferrer" aria-label="Messenger" className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-2xl hover:bg-[#00B2FF] transition-all">
                  <i className="ri-messenger-fill" aria-hidden="true"></i>
                </a>
              </div>
            </div>

            {/* Kolumna 3: Rechtliches (Wyrównanie do prawej) */}
            <div className="flex flex-col items-center md:items-end w-full text-center md:text-right">
              <Editable 
                isEditable={isEditable} 
                value={t.footer_legal_title} 
                onChange={(v) => onUpdate('footer_legal_title', v)} 
                as="h3" 
                className="text-lg font-bold mb-4 text-white" 
              />
              <ul className="space-y-3 text-slate-100 font-medium flex flex-col items-center md:items-end">
                <li>
                  <button onClick={() => setActiveModal('impressum')} className="hover:text-white transition-colors cursor-pointer">
                    <Editable isEditable={isEditable} value={t.footer_impressum} onChange={(v) => onUpdate('footer_impressum', v)} as="span" />
                  </button>
                </li>
                <li>
                  <button onClick={() => setActiveModal('datenschutz')} className="hover:text-white transition-colors cursor-pointer">
                    <Editable isEditable={isEditable} value={t.footer_datenschutz} onChange={(v) => onUpdate('footer_datenschutz', v)} as="span" />
                  </button>
                </li>
              </ul>
            </div>

          </div>

          <div className="text-center mt-8 text-slate-100 text-sm flex flex-col sm:flex-row justify-center items-center gap-2">
            <Editable isEditable={isEditable} value={t.footer_copyright} onChange={(v) => onUpdate('footer_copyright', v)} as="p" />
          </div>
        </div>
      </footer>

      {activeModal && (
        <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 transition-opacity duration-300">
          <div className="absolute inset-0 cursor-pointer" onClick={() => setActiveModal(null)}></div>
          <div className="relative bg-white w-full max-w-3xl max-h-[85vh] rounded-3xl shadow-2xl overflow-hidden flex flex-col transform transition-transform duration-300 scale-100">
            <div className="flex items-center justify-between px-8 py-6 border-b border-slate-100 bg-[#FAF8F5]">
              <h2 className="text-2xl font-extrabold text-[#125b60]">
                {activeModal === 'impressum' ? t.impressum_title : t.datenschutz_title}
              </h2>
              <button onClick={() => setActiveModal(null)} className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-slate-400 hover:text-[#c69c6d] transition-all cursor-pointer" aria-label={t.modal_close}>
                <i className="ri-close-line text-2xl" aria-hidden="true"></i>
              </button>
            </div>
            
            <div className="p-8 overflow-y-auto custom-scrollbar w-full">
               {formatLegalText(activeModal === 'impressum' ? t.impressum_content : t.datenschutz_content)}
            </div>
            
            <div className="px-8 py-4 border-t border-slate-100 bg-slate-50 text-right">
              <button onClick={() => setActiveModal(null)} className="px-6 py-2 bg-[#c69c6d] text-white font-bold rounded-lg hover:bg-[#b58b5c] transition-colors cursor-pointer">
                {t.modal_close}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}